/**
 * GenericArrival — Reusable arrival screen for any body area
 * Takes body-specific texts and glow configuration as props
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const GenericArrival = ({ texts, glowColor = 'rgba(220,185,135,0.3)', onContinue }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const delays = [0, 4000, 9000, 14000, 17000];
    const timers = delays.map((d, i) => setTimeout(() => setPhase(i), d));
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <motion.div
      className="fixed inset-0 flex flex-col items-center justify-center px-8"
      style={{ background: 'radial-gradient(ellipse at 50% 45%, rgba(25,18,10,1) 0%, rgba(10,7,4,1) 65%)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.5 }}
      data-testid="generic-arrival"
    >
      {/* Breathing glow */}
      <motion.div
        className="absolute pointer-events-none"
        style={{ top: '40%', left: '50%', transform: 'translate(-50%, -50%)' }}
        animate={{ width: [55, 80, 55], height: [55, 80, 55], opacity: [0.12, 0.3, 0.12] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
      >
        <div className="w-full h-full rounded-full" style={{
          background: `radial-gradient(circle, ${glowColor} 0%, transparent 65%)`,
          filter: 'blur(12px)',
        }} />
      </motion.div>
      <motion.div
        className="absolute pointer-events-none"
        style={{ top: '40%', left: '50%', transform: 'translate(-50%, -50%)' }}
        animate={{ width: [100, 140, 100], height: [100, 140, 100], opacity: [0.04, 0.1, 0.04] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
      >
        <div className="w-full h-full rounded-full" style={{
          background: `radial-gradient(circle, ${glowColor.replace('0.3', '0.15')} 0%, transparent 55%)`,
          filter: 'blur(18px)',
        }} />
      </motion.div>

      <div className="z-10 text-center max-w-sm">
        {/* Phase texts */}
        {texts.map((text, i) => (
          <AnimatePresence key={i}>
            {phase >= i && phase < texts.length && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: phase === i ? 1 : 0, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.5 }}
                className="absolute inset-0 flex items-center justify-center px-8"
              >
                <p className="text-white/40 text-base text-center leading-relaxed whitespace-pre-line"
                  style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
                  {text}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        ))}

        {/* Choice / Continue — after all texts */}
        {phase >= texts.length && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5 }}
            className="text-center"
          >
            <p className="text-white/40 text-base text-center px-8 leading-relaxed whitespace-pre-line mb-10"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
              {texts[texts.length - 1]}
            </p>
            <motion.button
              onClick={onContinue}
              className="px-8 py-3 rounded-full text-white/40 text-sm"
              style={{
                background: 'rgba(200,165,115,0.08)',
                border: '1px solid rgba(200,165,115,0.12)',
                fontFamily: "'Playfair Display', serif",
                fontStyle: 'italic',
              }}
              whileTap={{ scale: 0.97 }}
              data-testid="generic-arrival-continue-btn"
            >
              Continue
            </motion.button>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default GenericArrival;
